import React from 'react';
import RavenUtil from '../../../common/ravenUtil';
import PropTypes from 'prop-types';
import EnhancedVideoAccessTimeline from './EnhancedVideoAccessTimeline';
import EnhancedVideoAccessControlPanel from './EnhancedVideoAccessControlPanel';


export const EVA_TIMELINE_TIMESCALES = { // Roughed-in, timescale selector not fully implemented
    SECOND: {
        ID: "EVA_TIMELINE_TIMESCALE_SECOND",
        FACTOR: 10 // 10 pixels equals 1 second (600 pixels is 1 minute)
    },
    MINUTE: {
        ID: "EVA_TIMELINE_TIMESCALE_MINUTE",
        FACOTR: 1 // Default - 1 pixel equals 1 second (600 pixels is 10 minutes)
    },
    HOUR: {
        ID: "EVA_TIMELINE_TIMESCALE_HOUR",
        FACTOR: 0.1 // 1 pixel equals 10 seconds (600 pixels is 1 hr 20 mins)
    }
}

export const EVA_TIMELINE_DRAGGER_DURATION_PRESETS = {
    SECONDS_15: 15,
    SECONDS_30: 30,
    SECONDS_45: 45,
    SECONDS_60: 60
}

export const EVA_TIMELINE_DRAGGER_DEFAULT_DURATION_SECONDS = EVA_TIMELINE_DRAGGER_DURATION_PRESETS.SECONDS_15; // seconds = pixels at min scale


export default class EnhancedVideoAccess extends React.PureComponent {

    static propTypes = {
        stage: PropTypes.string.isRequired,
        ravenId: PropTypes.string.isRequired,
        cardId: PropTypes.string,
        cardVideoAvailabilityStartDate: PropTypes.instanceOf(Date),
        selectedEvent: PropTypes.object,
        route: PropTypes.arrayOf(PropTypes.object),
        startGeostamp: PropTypes.object,
        endGeostamp: PropTypes.object
    }

    state = {
        selectedDate: undefined,
        controlPanelRequestedSelectedDate: undefined,
        //timelineDraggerLeftCoord: EVA_TIMELINE_DRAGGER_DEFAULT_COORDINATE, // state updates are too costly during drag operation, even when rate-limited (timeline comp processes entire card events and gps data)
        timelineDraggerIsDragging: false,
        currentTimescaleId: EVA_TIMELINE_TIMESCALES.MINUTE.ID, // <-- one timescale supported so far (easiest starting point, looks good at 300 pixels == 5 mins, 1 pixel == 1 second)
        draggerDurationSeconds: EVA_TIMELINE_DRAGGER_DEFAULT_DURATION_SECONDS,
        isRequestDownloadModalOpen: false
    }

    goToEvent = (event) => {
        const eventDate = RavenUtil.toDate(event.ts);
        if (!eventDate) return;

        this.setState({
            selectedDate: eventDate,
            controlPanelRequestedSelectedDate: eventDate
        });
    }

    componentDidMount() {
        if (this.props.selectedEvent) {
            this.goToEvent(this.props.selectedEvent);
        }
    }
    componentDidUpdate(prevProps) {

        if (this.props.selectedEvent) {
            let shouldUpdateForEventSelect = false;

            if (!this.state.selectedDate && !this.state.controlPanelRequestedSelectedDate) {
                shouldUpdateForEventSelect = true;
            }

            const prevPropsSelectedEventId = prevProps.selectedEvent ? prevProps.selectedEvent.eventId : null;

            if (this.props.selectedEvent.eventId !== prevPropsSelectedEventId) {
                shouldUpdateForEventSelect = true;
            }

            if (shouldUpdateForEventSelect) {
                this.goToEvent(this.props.selectedEvent);
            }

        }
    }

    setTimelineDraggerIsDragging = (isDragging) => {
        this.setState({timelineDraggerIsDragging: isDragging});
    }

    setDraggerDurationSeconds = (durationSeconds) => {
        this.setState({draggerDurationSeconds: durationSeconds});
    }

    onDraggerMove = (selectedDate) => { // <-- On-EnhancedVideoAccessTimeline-Updating-Selected-Date (EVA Timeline comp doesn't )
        this.setState({
            selectedDate: selectedDate
        });
    }

    onRequestDownloadModalToggle = (isOpen) => {
        this.setState({isRequestDownloadModalOpen: !this.state.isRequestDownloadModalOpen});
    }

    onControlPanelSelectedDateChange = (controlPanelRequestedSelectedDate) => { // <-- a.k.a. setDraggerStartTime
        if (!controlPanelRequestedSelectedDate) {
            console.warn("EnhancedVideoAccess onControlPanelSelectedDateChange missing controlPanelRequestedSelectedDate.  Request ignored.");
            return;
        }
        this.setState({
            selectedDate: controlPanelRequestedSelectedDate, // prop for EnhancedVideoAccessControlPanel
            controlPanelRequestedSelectedDate: controlPanelRequestedSelectedDate // prop for EnhancedVideoAccessTimeline (selectedDate purposely not used for performance reasons)
        });
    }

    getDatesInRange = (startDate, endDate) => { // https://bobbyhadz.com/blog/javascript-get-all-dates-between-two-dates

        const date = new Date(startDate.getTime());
        if (this.props.selectedDate) {
            // update to selected time offset, if it's within the card's date time range
            const startDateTimeOffset = (date.getHours() * 60 * 60) + (date.getMinutes() * 60) + date.getSeconds();
            const selectedDateTimeOffset = (this.props.selectedDate.getHours() * 60 * 60) + (this.props.selectedDate.getMinutes() * 60) + this.props.selectedDate.getSeconds();
            if (selectedDateTimeOffset > startDateTimeOffset) {
                date.setHours(this.props.selectedDate.getHours());
                date.setMinutes(this.props.selectedDate.getMinutes());
                date.setSeconds(this.props.selectedDate.getSeconds());
            }
        }

        const dates = [];

        while (date <= endDate) {

            const newDate = new Date(date.getTime());
            if (this.props.selectedDate) {
                newDate.setHours(this.props.selectedDate.getHours());
                newDate.setMinutes(this.props.selectedDate.getMinutes());
                newDate.setSeconds(this.props.selectedDate.getSeconds());
            }

            dates.push(newDate);

            date.setDate(date.getDate() + 1);
        }

        dates.sort(function(a,b){
            // Turn your strings into dates, and then subtract them
            // to get a value that is either negative, positive, or zero.
            return a - b;
        });

        if (dates.length > 1) {

            const rangeEndDateTimeOffset = (dates[dates.length - 1].getHours() * 60 * 60) + (dates[dates.length - 1].getMinutes() * 60) + dates[dates.length - 1].getSeconds();
            const routeEndDateTimeOffset = (endDate.getHours() * 60 * 60) + (endDate.getMinutes() * 60) + endDate.getSeconds();

            if (rangeEndDateTimeOffset > routeEndDateTimeOffset) {
                dates[dates.length - 1].setHours(endDate.getHours());
                dates[dates.length - 1].setMinutes(endDate.getMinutes());
                dates[dates.length - 1].setSeconds(endDate.getSeconds());
            }

        }         
        return dates;
    }

    render() {
        if (!this.props.startGeostamp && !this.props.endGeostamp) {
            // progress spinner here?
            return null;
        }
        const routeStart = this.props.startGeostamp;
        const cardStartTs = routeStart.ts;
        const cardStartDate = RavenUtil.toDate(cardStartTs);

        const routeEnd = this.props.endGeostamp;
        const cardEndTs = routeEnd.ts;
        const cardEndDate = cardEndTs ? RavenUtil.toDate(cardEndTs) : new Date();

        const availableDates = this.getDatesInRange(cardStartDate, cardEndDate);

        const availableDatesSelectLookup = {}
        availableDates.forEach( (dateWithTimeOffset) => {
            const [justIOSDate] = dateWithTimeOffset.toISOString().split('T');
            availableDatesSelectLookup[justIOSDate] = dateWithTimeOffset;
        });

        let selectedDate = this.state.selectedDate;
        if (!selectedDate) {
            selectedDate = availableDates[0];
        }

        return (
            <div className="enhanced-video-access-panel"
                onMouseUp={() => { this.setTimelineDraggerIsDragging(false) }}
                onMouseLeave={() => { this.setTimelineDraggerIsDragging(false) }}
            >
                <EnhancedVideoAccessTimeline
                    {...this.props}
                    cardStartDate = {cardStartDate}
                    cardEndDate = {cardEndDate}
                    //selectedDate <-- do not include (performance killer since dragger sets this prop for other components, rate-limited yes but still performance killer)
                    controlPanelRequestedSelectedDate = {this.state.controlPanelRequestedSelectedDate} // special prop to allow control panel to move the dragger in realtime with manual form updates (rate-limited simply by human using slow interface)
                    timelineDraggerIsDragging = {this.state.timelineDraggerIsDragging}
                    setTimelineDraggerIsDragging = {this.setTimelineDraggerIsDragging}
                    currentTimescaleId = {this.state.currentTimescaleId}
                    draggerDurationSeconds = {this.state.draggerDurationSeconds}
                    onDraggerMove = {this.onDraggerMove}
                    isRequestDownloadModalOpen = {this.state.isRequestDownloadModalOpen}
                />
                <EnhancedVideoAccessControlPanel
                    toggleEnhancedVideoAccessModeActive = {this.props.toggleEnhancedVideoAccessModeActive}
                    // selectedDate = {selectedDate} <-- redundant
                    stage = {this.props.stage}
                    ravenId = {this.props.ravenId}
                    controlPanelRequestedSelectedDate = {selectedDate}
                    setDraggerStartTime = {this.onControlPanelSelectedDateChange}
                    availableDatesSelectLookup = {availableDatesSelectLookup}
                    cardStartDate = {cardStartDate}
                    cardEndDate = {cardEndDate}
                    draggerDurationSeconds = {this.state.draggerDurationSeconds}
                    setDraggerDurationSeconds = {this.setDraggerDurationSeconds}
                    onRequestDownloadModalToggle = {this.onRequestDownloadModalToggle}
                />
            </div>
        );
    }
}
